// TurnstileWidget — Cloudflare Turnstile CAPTCHA, opt-in via
// VITE_TURNSTILE_SITE_KEY. When the env var is absent, renders null
// and treats the parent as "no CAPTCHA needed".
//
// Loads the Turnstile script once on first mount; reuses the singleton
// for subsequent widgets. Parent receives the token via onChange and
// submits it to Supabase Auth in the `captchaToken` option.

import { useEffect, useRef } from 'react';

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface TurnstileApi {
  render(container: HTMLElement, opts: {
    sitekey: string;
    callback: (token: string) => void;
    'expired-callback'?: () => void;
    'error-callback'?: () => void;
    theme?: 'light' | 'dark' | 'auto';
  }): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptInjected = false;
const scriptReadyListeners: Array<() => void> = [];

function injectTurnstileScript(onReady: () => void): void {
  if (typeof window === 'undefined') return;
  if (window.turnstile) { onReady(); return; }
  scriptReadyListeners.push(onReady);
  if (scriptInjected) return;
  scriptInjected = true;
  const tag = document.createElement('script');
  tag.src = SCRIPT_URL;
  tag.async = true;
  tag.defer = true;
  tag.onload = () => {
    for (const cb of scriptReadyListeners.splice(0)) cb();
  };
  document.head.appendChild(tag);
}

interface Props {
  onChange: (token: string | null) => void;
}

export function TurnstileWidget({ onChange }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    injectTurnstileScript(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => onChange(token),
        'expired-callback': () => onChange(null),
        'error-callback': () => onChange(null),
        theme: 'auto',
      });
    });
    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      if (id && window.turnstile) {
        try { window.turnstile.remove(id); } catch { /* ignore */ }
      }
    };
    // onChange may change between renders but we don't want to re-init
    // — Turnstile widgets are heavy. Parent should memoise the cb.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} data-testid="turnstile-widget" className="my-2" />;
}

export const TURNSTILE_ENABLED = Boolean(SITE_KEY);
